import {
	ArrowLeftOutlined,
	DeleteOutlined,
	EditOutlined,
	InfoCircleOutlined,
	PlusOutlined,
} from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Form, Input, Modal, message } from "antd";
import { useState } from "react";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import type { AppRole } from "@/shared/config/navigation";
import { canManageObjectStructure } from "@/shared/config/permissions";
import { parseApiError } from "@/shared/utils/error";
import { blocksApi } from "../api/blocks.api";
import { buildingsApi } from "../api/buildings.api";
import { sectionsApi } from "../api/sections.api";
import type { Section } from "../types/objects.types";

export function SectionsPage() {
	const { buildingId, blockId } = useParams();
	const navigate = useNavigate();
	const { role } = useOutletContext<{ role: AppRole }>();
	const queryClient = useQueryClient();
	const canManageSections = canManageObjectStructure(role);
	const [modalVisible, setModalVisible] = useState(false);
	const [editModalVisible, setEditModalVisible] = useState(false);
	const [form] = Form.useForm<{ name: string }>();
	const [editForm] = Form.useForm<{ name: string }>();
	const [editingSectionId, setEditingSectionId] = useState<string | null>(null);
	const currentBlockId = blockId ?? "";

	const { data: building, isLoading: isBuildingLoading } = useQuery({
		queryKey: ["building", buildingId],
		queryFn: () => buildingsApi.getBuilding(buildingId ?? ""),
		enabled: !!buildingId,
	});

	const roomsMapBaseParams = building?.orgId
		? `orgId=${building.orgId}&buildingId=${building.id}`
		: `buildingId=${buildingId}`;

	const { data: block, isLoading: isBlockLoading } = useQuery({
		queryKey: ["block", blockId],
		queryFn: () => blocksApi.getBlock(currentBlockId),
		enabled: !!blockId,
	});

	const { data: blockSections = [] } = useQuery({
		queryKey: ["sections", blockId],
		queryFn: () => sectionsApi.getSections({ blockId, limit: 100 }),
		enabled: !!blockId,
	});

	const createSectionMutation = useMutation({
		mutationFn: sectionsApi.createSection,
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: ["sections", blockId] });
			message.success("Sektsiya muvaffaqiyatli qo'shildi!");
			setModalVisible(false);
			form.resetFields();
		},
		onError: (error: unknown) => {
			message.error(parseApiError(error) || "Sektsiya yaratishda xatolik yuz berdi");
		},
	});

	const updateSectionMutation = useMutation({
		mutationFn: ({ id, name }: { id: string; name: string }) =>
			sectionsApi.updateSection(id, { name }),
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: ["sections", blockId] });
			message.success("Sektsiya muvaffaqiyatli yangilandi!");
			setEditModalVisible(false);
			setEditingSectionId(null);
			editForm.resetFields();
		},
		onError: (error: unknown) => {
			message.error(parseApiError(error) || "Sektsiya yangilashda xatolik yuz berdi");
		},
	});

	const deleteSectionMutation = useMutation({
		mutationFn: sectionsApi.deleteSection,
		onMutate: async (sectionId) => {
			await queryClient.cancelQueries({ queryKey: ["sections", blockId] });
			const previousSections = queryClient.getQueryData<Section[]>(["sections", blockId]);
			queryClient.setQueryData<Section[]>(["sections", blockId], (old) =>
				old ? old.filter((s) => s.id !== sectionId) : []
			);
			return { previousSections };
		},
		onSuccess: async () => {
			await queryClient.invalidateQueries({
				queryKey: ["sections", blockId],
				refetchType: "inactive",
			});
			message.success("Sektsiya muvaffaqiyatli o'chirildi!");
		},
		onError: (_error, _sectionId, context) => {
			if (context?.previousSections) {
				queryClient.setQueryData(["sections", blockId], context.previousSections);
			}
			message.error("Xatolik: Sektsiyada xonalar mavjud bo'lsa uni o'chirib bo'lmaydi!");
		},
	});

	if (isBlockLoading || isBuildingLoading) {
		return <div className="crm-card pad empty">Yuklanmoqda...</div>;
	}

	if (!block) {
		return (
			<div className="crm-card pad empty">
				Blok topilmadi.
				<Button className="crm-mt" onClick={() => navigate(`/objects/${buildingId}/blocks`)}>
					Orqaga
				</Button>
			</div>
		);
	}

	const handleCreateSection = () => {
		if (!canManageSections) {
			message.warning("Menejer sektsiya strukturasini o'zgartira olmaydi.");
			return;
		}

		form.validateFields().then((values) => {
			createSectionMutation.mutate({
				blockId: block.id,
				name: values.name,
			});
		});
	};

	const handleDeleteSection = (sectionId: string, name: string) => {
		if (!canManageSections) {
			message.warning("Menejer sektsiya strukturasini o'zgartira olmaydi.");
			return;
		}

		Modal.confirm({
			title: "Sektsiyani o'chirish",
			content: `Rostdan ham "${name}" sektsiyasini o'chirmoqchimisiz?`,
			okText: "O'chirish",
			okType: "danger",
			cancelText: "Bekor qilish",
			onOk: () => {
				deleteSectionMutation.mutate(sectionId);
			},
		});
	};

	const handleEditSection = (sectionId: string, name: string) => {
		if (!canManageSections) {
			message.warning("Menejer sektsiya strukturasini o'zgartira olmaydi.");
			return;
		}

		setEditingSectionId(sectionId);
		editForm.setFieldsValue({ name });
		setEditModalVisible(true);
	};

	const handleUpdateSection = () => {
		if (!editingSectionId) {
			return;
		}
		editForm.validateFields().then((values) => {
			updateSectionMutation.mutate({ id: editingSectionId, name: values.name });
		});
	};

	return (
		<div className="crm-sections">
			<div className="crm-page-head">
				<div>
					<nav className="crm-breadcrumb">
						<button
							type="button"
							className="crm-breadcrumb-item"
							onClick={() => navigate("/objects")}
						>
							Binolar
						</button>
						<span className="crm-breadcrumb-sep">/</span>
						<button
							type="button"
							className="crm-breadcrumb-item"
							onClick={() => navigate(`/objects/${buildingId}/blocks`)}
						>
							{building?.name || "Bino"}
						</button>
						<span className="crm-breadcrumb-sep">/</span>
						<span className="crm-breadcrumb-current">{block.name}</span>
					</nav>
					<div className="crm-page-title-row">
						<Button
							type="text"
							icon={<ArrowLeftOutlined />}
							className="crm-back-btn"
							onClick={() => navigate(`/objects/${buildingId}/blocks`)}
						/>
						<h1 style={{ margin: 0 }}>{block.name} — sektsiyalar</h1>
					</div>
					<p style={{ color: "var(--crm-ink-3)", marginTop: "4px", marginLeft: "44px" }}>
						{blockSections.length} ta sektsiya
					</p>
				</div>
				{canManageSections ? (
					<Button
						className="crm-primary-btn"
						icon={<PlusOutlined />}
						onClick={() => setModalVisible(true)}
					>
						Sektsiya qo'shish
					</Button>
				) : null}
			</div>

			<div className="grid g3 crm-sections-grid">
				{blockSections.map((section) => (
					<div className="crm-card" key={section.id}>
						<div className="crm-card-body">
							<div className="row between center">
								<strong style={{ fontSize: "16px" }}>{section.name}</strong>
								{canManageSections ? (
									<div className="row gap-s" style={{ gap: "4px" }}>
										<Button
											type="text"
											icon={<EditOutlined />}
											onClick={() => handleEditSection(section.id, section.name)}
											size="small"
										/>
										<Button
											type="text"
											danger
											icon={<DeleteOutlined />}
											onClick={() => handleDeleteSection(section.id, section.name)}
											size="small"
										/>
									</div>
								) : null}
							</div>
							<div style={{ fontSize: "13px", color: "var(--crm-ink-3)", marginTop: "8px" }}>
								{section.roomsCount} ta xona
							</div>
							<Button
								className="crm-mt"
								style={{ width: "100%", justifyContent: "center" }}
								onClick={() =>
									navigate(
										`/rooms?${roomsMapBaseParams}&blockId=${blockId}&sectionId=${section.id}`,
										{ state: { returnTo: `/objects/${buildingId}/blocks/${blockId}/sections` } }
									)
								}
							>
								Xonalar xaritasi
							</Button>
						</div>
					</div>
				))}

				{canManageSections ? (
					<button
						type="button"
						className="crm-card"
						style={{
							borderStyle: "dashed",
							cursor: "pointer",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							background: "transparent",
							width: "100%",
							textAlign: "center",
						}}
						onClick={() => setModalVisible(true)}
					>
						<div className="crm-card-body empty" style={{ padding: "28px" }}>
							<PlusOutlined style={{ fontSize: "24px", color: "var(--crm-ink-3)" }} />
							<div className="crm-mt-s" style={{ fontWeight: "700", marginTop: "8px" }}>
								Sektsiya qo'shish
							</div>
						</div>
					</button>
				) : null}
			</div>

			{canManageSections ? (
				<div className="crm-card crm-mt" style={{ marginTop: "16px" }}>
					<div
						className="crm-card-body row center gap-s muted"
						style={{ display: "flex", gap: "8px", alignItems: "center", color: "var(--crm-ink-2)" }}
					>
						<InfoCircleOutlined style={{ color: "var(--crm-amber)" }} />
						<span>O'chirish faqat xonalari bo'lmaganda mumkin (aks holda 422 — biznes qoida).</span>
					</div>
				</div>
			) : null}

			<Modal
				title="Yangi sektsiya (padezd)"
				open={modalVisible}
				onOk={handleCreateSection}
				onCancel={() => {
					form.resetFields();
					setModalVisible(false);
				}}
				okText="Saqlash"
				cancelText="Bekor"
				okButtonProps={{ className: "crm-primary-btn", loading: createSectionMutation.isPending }}
			>
				<Form
					form={form}
					layout="vertical"
					initialValues={{ name: `${blockSections.length + 1}-padezd` }}
					style={{ marginTop: "16px" }}
				>
					<Form.Item
						name="name"
						label="Sektsiya nomi"
						rules={[{ required: true, message: "Sektsiya nomini kiriting!" }]}
					>
						<Input placeholder="Masalan: 3-padezd" />
					</Form.Item>
				</Form>
			</Modal>

			<Modal
				title="Sektsiyani tahrirlash"
				open={editModalVisible}
				onOk={handleUpdateSection}
				onCancel={() => {
					editForm.resetFields();
					setEditModalVisible(false);
					setEditingSectionId(null);
				}}
				okText="Saqlash"
				cancelText="Bekor"
				okButtonProps={{ className: "crm-primary-btn", loading: updateSectionMutation.isPending }}
			>
				<Form form={editForm} layout="vertical" style={{ marginTop: "16px" }}>
					<Form.Item
						name="name"
						label="Sektsiya nomi"
						rules={[{ required: true, message: "Sektsiya nomini kiriting!" }]}
					>
						<Input placeholder="Masalan: 3-padezd" />
					</Form.Item>
				</Form>
			</Modal>
		</div>
	);
}
